import React, { useState } from 'react';
import { MainLayout } from '../../components/layout/MainLayout';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { employeeService } from '../../api/services/employeeService';
import { Employee, EmployeeInput } from '@shared/types';
import { EmployeeType } from '@shared/types/Employee';
import { toast } from 'react-hot-toast';

export default function EmployeeManagement() {
  const queryClient = useQueryClient();
  const [selectedType, setSelectedType] = useState<number>(EmployeeType.Sewer);
  const [formData, setFormData] = useState<EmployeeInput>({
    FirstName: '',
    LastName: '',
    Tag: 0,
    EmployeeType: EmployeeType.Sewer,
  });
  const [sortField, setSortField] = useState<'FirstName' | 'LastName' | 'Tag'>('FirstName');

  // Fetch employees by type
  const { data: employees, isLoading } = useQuery({
    queryKey: ['employees', selectedType],
    queryFn: () => employeeService.getEmployeesByType(selectedType),
  });

  // Create employee mutation
  const createMutation = useMutation({
    mutationFn: (employee: EmployeeInput) => employeeService.createEmployee(employee),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees', selectedType] });
      toast.success('Employee added successfully');
      setFormData({
        FirstName: '',
        LastName: '',
        Tag: 0,
        EmployeeType: selectedType,
      });
    },
    onError: () => {
      toast.error('Failed to add employee');
    },
  });

  // Delete employee mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => employeeService.deleteEmployee(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees', selectedType] });
      toast.success('Employee deleted successfully');
    },
    onError: () => {
      toast.error('Failed to delete employee');
    },
  });

  const handleTypeChange = (type: number) => {
    setSelectedType(type);
    setFormData({
      ...formData,
      EmployeeType: type,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.FirstName || !formData.LastName || !formData.Tag) {
      toast.error('Please fill in all fields');
      return;
    }

    createMutation.mutate({
      ...formData,
      EmployeeType: selectedType,
    });
  };

  const handleDelete = (employee: Employee) => {
    if (window.confirm(`Are you sure you want to delete ${employee.FirstName} ${employee.LastName}?`)) {
      deleteMutation.mutate(employee.Id);
    }
  };

  // Sort employees
  const sortedEmployees = React.useMemo(() => {
    if (!employees) return [];
    return [...employees].sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return aVal.localeCompare(bVal);
      }
      return (aVal as number) - (bVal as number);
    });
  }, [employees, sortField]);

  return (
    <MainLayout>
      <div className="px-4 py-6 sm:px-0">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Manage Employees
            </h2>
          </div>

          <div className="p-6">
            {/* Worker Type Selector */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Worker Type:
              </label>
              <select
                value={selectedType}
                onChange={(e) => handleTypeChange(parseInt(e.target.value))}
                className="block w-full sm:w-64 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-gray-100"
              >
                <option value={EmployeeType.Sewer}>Sewer</option>
                <option value={EmployeeType.Trimmer}>Trimmer</option>
              </select>
            </div>

            {/* Add Employee Form */}
            <div className="mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Add Employee
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-0 sm:flex sm:gap-4 sm:items-end">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    First Name
                  </label>
                  <input
                    type="text"
                    value={formData.FirstName}
                    onChange={(e) => setFormData({ ...formData, FirstName: e.target.value })}
                    placeholder="First Name"
                    required
                    className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>

                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={formData.LastName}
                    onChange={(e) => setFormData({ ...formData, LastName: e.target.value })}
                    placeholder="Last Name"
                    required
                    className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>

                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Tag ID
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.Tag || ''}
                    onChange={(e) => setFormData({ ...formData, Tag: parseInt(e.target.value) || 0 })}
                    placeholder="ID"
                    required
                    className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>

                <div>
                  <button
                    type="submit"
                    disabled={createMutation.isPending}
                    className="w-full sm:w-auto px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {createMutation.isPending ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </form>
            </div>

            {/* Employees Table */}
            <div className="overflow-x-auto mb-6">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th
                      onClick={() => setSortField('FirstName')}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:text-gray-700 dark:hover:text-gray-100"
                    >
                      First Name {sortField === 'FirstName' && '↓'}
                    </th>
                    <th
                      onClick={() => setSortField('LastName')}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:text-gray-700 dark:hover:text-gray-100"
                    >
                      Last Name {sortField === 'LastName' && '↓'}
                    </th>
                    <th
                      onClick={() => setSortField('Tag')}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:text-gray-700 dark:hover:text-gray-100"
                    >
                      Tag ID {sortField === 'Tag' && '↓'}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {isLoading ? (
                    <tr>
                      <td colSpan={4} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                        Loading...
                      </td>
                    </tr>
                  ) : sortedEmployees.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                        No employees found
                      </td>
                    </tr>
                  ) : (
                    sortedEmployees.map((employee) => (
                      <tr key={employee.Id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {employee.FirstName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {employee.LastName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {employee.Tag}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <button
                            onClick={() => handleDelete(employee)}
                            className="px-3 py-1 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
